import { AsyncButton, PauseCover } from "aic";
import { CommentableEntityType } from "functions/api/notifications/interface";
import { ReactNode } from "react";
import { Col, Container, Offcanvas, Row } from "react-bootstrap";
import { ChatLeftDots, ChatLeftDotsFill } from "react-bootstrap-icons";
import { useLocation } from "react-router-dom";
import { CommentFilter } from "./CommentFilter";
import { CommentThreads } from "./CommentThreads";
import "./Comments.css";
import { CommentSubscriptionButton } from "./CommentsSubscriptionButton";
import { CreateCommentButton } from "./CreateCommentButton";
import { ResolveAllCommentsButton } from "./ResolveAllCommentsButton";
import { isRedirectingFromNotifications } from "./isRedirectingFromNotifications";
import { useComments } from "./useComments";

interface CommentPanelProps {
  activeContext: CommentableEntityType;
  asButton?: boolean;
}

/**
 * Inner content of the comment panel with controls and comment threads.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 */
export const CommentPanelInner = ({ activeContext }: CommentPanelProps): React.ReactElement => {
  // Get contexts
  const {
    isSubscribed,
    toggleCommentsSubscription,
    toggleCommentsSubscriptionRequest: toggleSubscriptionStatusRequest,
  } = useComments(activeContext as CommentableEntityType);

  return (
    <Container>
      <Row>
        <Col>
          <div className="comment-button-header-panel d-flex flex-row justify-content-between">
            <CreateCommentButton activeContext={activeContext} />
            <div className="d-flex justify-content-end">
              <CommentSubscriptionButton
                toggleCommentsSubscription={toggleCommentsSubscription}
                isSubscribed={isSubscribed}
                toggleSubscriptionStatusRequest={toggleSubscriptionStatusRequest}
              />
              <ResolveAllCommentsButton activeContext={activeContext} />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <CommentFilter activeContext={activeContext} />
        </Col>
      </Row>
      <Row>
        <Col>
          <CommentThreads activeContext={activeContext} />
        </Col>
      </Row>
    </Container>
  );
};

CommentPanelInner.displayName = "CommentPanelInner";

/**
 * Icon for the comment button. Filled variant when comments exist.
 *
 * @param props - Component props.
 * @param props.anyComments - When true, show the filled icon.
 */
const CommentIcon = (props: { anyComments: boolean }): ReactNode => {
  return props.anyComments ? <ChatLeftDotsFill /> : <ChatLeftDots />;
};

CommentIcon.displayName = "CommentIcon";

/**
 * Displays the comment panel as an offcanvas sidebar or button.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 * @param props.asButton - When true, render as a button.
 */
export const CommentPanel = ({ activeContext, asButton = false }: CommentPanelProps): ReactNode => {
  // Get contexts
  const { comments, commentPanelStatus, setCommentPanelStatus, title } = useComments(
    activeContext as CommentableEntityType,
  );
  const location = useLocation();

  // Component render
  return !comments || !commentPanelStatus || !setCommentPanelStatus ? null : (
    <>
      {asButton ? (
        <AsyncButton
          id="tlf-comments-button"
          aria-label={title}
          variant="primary"
          size="sm"
          className="d-flex align-items-center gap-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCommentPanelStatus({
              ...commentPanelStatus,
              showComments: !commentPanelStatus.showComments,
            });
          }}
        >
          <CommentIcon anyComments={comments.length > 0} />
          {title}
        </AsyncButton>
      ) : (
        <div
          className="comment-panel-button align-self-start ms-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCommentPanelStatus({
              ...commentPanelStatus,
              showComments: !commentPanelStatus.showComments,
            });
          }}
          aria-label={title}
          title={title}
        >
          <CommentIcon anyComments={comments.length > 0} />
        </div>
      )}
      <Offcanvas
        show={commentPanelStatus.showComments}
        placement="end"
        onHide={() =>
          setCommentPanelStatus({
            ...commentPanelStatus,
            showComments: false,
          })
        }
        backdrop={false}
        scroll={true}
      >
        {isRedirectingFromNotifications(location.search) && (
          <PauseCover text="Redirecting to comment..." />
        )}
        <Offcanvas.Header
          closeButton
          className="comments-offcanvas-header"
        >
          <Offcanvas.Title className="comments-offcanvas-title me-2">{title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="comments-offcanvas-body">
          <CommentPanelInner activeContext={activeContext} />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

CommentPanel.displayName = "CommentPanel";
